'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { push, replace } from 'DesignData/actions/History';
import { pick, flatten } from 'underscore';
import { FINDER_FILE_DELETE, FINDER_FILE_DUPLICATE, FINDER_FILE_RENAME, FINDER_FOLDER_COPY_PATH, FINDER_FOLDER_MOVE_FILE, FINDER_FILE_SHOW_INFORMATION, FINDER_FOLDER_DELETE, FINDER_FOLDER_DUPLICATE, FINDER_FOLDER_OPEN_EDITOR, FINDER_FOLDER_RENAME, FINDER_FOLDER_TOGGLE_LOCK, FINDER_BULK_DELETE, FINDER_BULK_MOVE, FINDER_FILE_SHOW_DEPENDENTS, FINDER_FILE_SHOW_REVISIONS, FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE, FINDER_FILE_CREATE_PAGE, FINDER_FILE_CREATE_EMAIL, FINDER_FILE_COPY_PATH, FINDER_FILE_COPY_PUBLIC_URL, FINDER_FILE_COPY_MODULE_SNIPPET, FINDER_FILE_VIEW_LAYOUT_SOURCE, FINDER_ASSETS_PORTAL_COPY, FINDER_BULK_PORTAL_COPY, FINDER_THEMES_DUPLICATE, FINDER_THEMES_CREATE_CHILD, FINDER_THEMES_CHILD_ADD_FILE } from 'DesignData/actions/ActionTypes';
import { FOLDER } from 'DesignData/enums/DesignAssetTypes';
import { FilePropTypes, FolderPropTypes, AssetActionsPropTypes } from 'DesignData/propTypes/finder';
import { createPath } from 'DesignData/helpers/routingHelper';
import { isFolderEditorActive } from 'DesignData/selectors/app';
import { getHasMultipleSelections, getFolder, getHasMarketplaceListing, getIsRootFolder, getIsThemeRootFolder, getSelectedAssetsCount, makeGetAssetActions, makeGetAssetPermissions, makeGetIsAssetMultiSelected, getIsLocked } from 'DesignData/selectors/finder';
import * as FilesActions from 'DesignData/actions/finder/Files';
import * as OptionsActions from 'DesignData/actions/finder/Options';
import * as FoldersActions from 'DesignData/actions/finder/Folders';

function makeMapStateToProps() {
  const getAssetActions = makeGetAssetActions();
  const getAssetPermissions = makeGetAssetPermissions();
  const getIsAssetMultiSelected = makeGetIsAssetMultiSelected();
  return function mapStateToProps(state, props) {
    const {
      fileId,
      folderId
    } = props;
    const hasMultipleSelections = getHasMultipleSelections(state);
    const isMultiSelected = getIsAssetMultiSelected(state, props);
    const isRootFolder = folderId && getIsRootFolder(state, props);
    let selectedAssetsCount = getSelectedAssetsCount(state);

    if (fileId || folderId) {
      selectedAssetsCount = isMultiSelected || isRootFolder && hasMultipleSelections ? selectedAssetsCount : null;
    }

    return {
      folder: getFolder(state, props),
      assetActions: getAssetActions(state, props),
      assetPermissions: getAssetPermissions(state, props),
      selectedAssetsCount,
      hasMarketplaceListing: getHasMarketplaceListing(state, props),
      isFolderEditorActive: isFolderEditorActive(state),
      isLocked: folderId && getIsLocked(state, props),
      isThemeRootFolder: getIsThemeRootFolder(state, props)
    };
  };
}

const mapDispatchToProps = {
  copyFolderPath: FoldersActions.copyPath,
  duplicateFile: FilesActions.duplicateFile,
  duplicateFolder: FoldersActions.duplicateFolder,
  duplicateLayoutToTemplate: FilesActions.duplicateLayoutToTemplate,
  showRevisions: FilesActions.showRevisions,
  toggleRenameAsset: OptionsActions.toggleRenameAsset,
  showDependents: FilesActions.showDependents,
  copyFilePath: FilesActions.copyPath,
  copyPublicUrl: FilesActions.copyPublicUrl,
  copyModuleSnippet: FilesActions.copyModuleSnippet,
  viewLayoutSource: FilesActions.viewLayoutSource,
  pushRoute: push,
  replaceRoute: replace,
  toggleLockFolder: FoldersActions.toggleLockFolder
};

const mergeProps = (stateProps, dispatchProps, ownProps) => {
  const {
    fileId,
    folderId,
    parentFolderId,
    file
  } = ownProps;
  const {
    folder
  } = stateProps;
  const {
    assetActions
  } = stateProps;
  const actionsList = flatten([assetActions.primaryActions, assetActions.secondaryActions, assetActions.deleteAction]);
  const actionHandlers = pick({
    [FINDER_FILE_DUPLICATE]: () => dispatchProps.duplicateFile(fileId),
    [FINDER_FILE_RENAME]: () => dispatchProps.toggleRenameAsset(fileId),
    [FINDER_FOLDER_COPY_PATH]: () => dispatchProps.copyFolderPath(folderId),
    [FINDER_FOLDER_DUPLICATE]: () => dispatchProps.duplicateFolder(folder, parentFolderId),
    [FINDER_FOLDER_RENAME]: () => dispatchProps.toggleRenameAsset(folderId),
    [FINDER_FOLDER_OPEN_EDITOR]: () => {
      const folderRoute = createPath(FOLDER, folderId);

      if (stateProps.isFolderEditorActive) {
        dispatchProps.replaceRoute(folderRoute);
      } else {
        dispatchProps.pushRoute(folderRoute);
      }
    },
    [FINDER_FOLDER_TOGGLE_LOCK]: () => dispatchProps.toggleLockFolder(folderId),
    [FINDER_FILE_SHOW_DEPENDENTS]: () => dispatchProps.showDependents(fileId),
    [FINDER_FILE_SHOW_REVISIONS]: () => dispatchProps.showRevisions(fileId),
    [FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE]: () => dispatchProps.duplicateLayoutToTemplate(fileId),
    [FINDER_FILE_COPY_PATH]: () => dispatchProps.copyFilePath(fileId),
    [FINDER_FILE_COPY_PUBLIC_URL]: () => dispatchProps.copyPublicUrl(file),
    [FINDER_FILE_COPY_MODULE_SNIPPET]: () => dispatchProps.copyModuleSnippet(fileId),
    [FINDER_FILE_VIEW_LAYOUT_SOURCE]: () => dispatchProps.viewLayoutSource(fileId),
    [FINDER_FILE_SHOW_INFORMATION]: ownProps.onShowInformationClick,
    [FINDER_FOLDER_MOVE_FILE]: ownProps.onMoveFileClick,
    [FINDER_FILE_DELETE]: ownProps.onDeleteFileClick,
    [FINDER_BULK_MOVE]: ownProps.onBulkMoveFileClick,
    [FINDER_BULK_DELETE]: ownProps.onBulkDeleteClick,
    [FINDER_FOLDER_DELETE]: ownProps.onDeleteFolderClick,
    [FINDER_FILE_CREATE_PAGE]: ownProps.onCreateContentClick,
    [FINDER_FILE_CREATE_EMAIL]: ownProps.onCreateContentClick,
    [FINDER_ASSETS_PORTAL_COPY]: ownProps.onAssetPortalCopyClick,
    [FINDER_BULK_PORTAL_COPY]: ownProps.onAssetPortalCopyClick,
    [FINDER_THEMES_DUPLICATE]: ownProps.onThemeDuplicateClick,
    [FINDER_THEMES_CREATE_CHILD]: ownProps.onCreateChildThemeClick,
    [FINDER_THEMES_CHILD_ADD_FILE]: ownProps.onChildThemeAddClick
  }, actionsList);
  return Object.assign({}, stateProps, {}, ownProps, {
    actionHandlers
  });
};

export default function withAssetActions(WrappedComponent) {
  class WithAssetActions extends Component {
    render() {
      const {
        assetActions,
        assetPermissions,
        actionHandlers,
        fileName,
        fileType,
        folderName,
        hasMarketplaceListing,
        selectedAssetsCount,
        isLocked
      } = this.props;
      const i18nOptions = {
        fileName,
        count: selectedAssetsCount,
        folderName,
        fileType
      };

      const i18nKey = key => {
        if (key.includes(FINDER_FOLDER_TOGGLE_LOCK)) {
          return `DesignFinderUI.actions.menuActions.${key}.${isLocked ? 'unlockText' : 'lockText'}`;
        }

        return `DesignFinderUI.actions.menuActions.${key}.text`;
      };

      return /*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, {
        assetActions: assetActions,
        actionHandlers: actionHandlers,
        hasMarketplaceListing: hasMarketplaceListing,
        i18nOptions: i18nOptions,
        i18nKey: i18nKey,
        permissions: assetPermissions
      }));
    }

  }

  WithAssetActions.propTypes = {
    assetActions: AssetActionsPropTypes.assetActions.isRequired,
    actionHandlers: AssetActionsPropTypes.actionHandlers.isRequired,
    assetPermissions: AssetActionsPropTypes.assetPermissions.isRequired,
    fileType: FilePropTypes.fileType,
    fileName: PropTypes.string,
    fileId: FilePropTypes.fileId,
    folderId: FolderPropTypes.folderId,
    folderName: PropTypes.string,
    hasMarketplaceListing: PropTypes.bool,
    isLocked: PropTypes.bool,
    selectedAssetsCount: PropTypes.number
  };
  WithAssetActions.defaultProps = {
    selectedAssetsCount: 0
  };
  return connect(makeMapStateToProps, mapDispatchToProps, mergeProps)(WithAssetActions);
}